import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, Award, Trophy, Star, Medal, Quote, Target, Briefcase } from 'lucide-react';
import { awards, founderStory, founderAwards } from '../mock';

const Awards = () => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, follow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Awards &amp;
              <br />
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Recognition
              </span>
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              With over 30 years of experience in the automobile industry, Mr. Vinod Parmar has received
              multiple recognitions and awards for excellence in technical expertise, customer satisfaction
              and automotive training.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder's Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2 block">Founder's Story</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">The Man Behind Carz Street</h2>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Photo + card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 lg:sticky lg:top-28"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src={founderStory.photo} alt={founderStory.name} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
              </div>
              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 mt-6 text-white">
                <h3 className="text-2xl font-bold">{founderStory.name}</h3>
                <p className="text-white/90">{founderStory.role}</p>
              </div>
            </motion.div>

            {/* Story text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3 space-y-5 text-gray-700 text-lg leading-relaxed"
            >
              <p className="text-xl text-gray-800 font-medium">{founderStory.intro}</p>
              {founderStory.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </motion.div>
          </div>

          {/* Career Timeline */}
          <div className="mt-20">
            <div className="flex items-center gap-3 mb-10 justify-center">
              <Briefcase className="w-7 h-7 text-red-600" />
              <h3 className="text-3xl font-bold text-gray-900">Professional Journey</h3>
            </div>
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 md:-translate-x-1/2" />
              {founderStory.timeline.map((item, index) => (
                <motion.div
                  key={index}
                  className={`relative flex items-start mb-8 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-12 md:ml-0' : 'md:pl-12 md:ml-auto'
                  } pl-12 md:pl-0`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className={`absolute left-4 md:left-auto ${index % 2 === 0 ? 'md:-right-2' : 'md:-left-2'} w-4 h-4 bg-red-600 rounded-full ring-4 ring-red-100 -translate-x-1/2 md:translate-x-0 mt-1.5`} />
                  <div className={`bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow duration-300 w-full ${index % 2 === 0 ? 'md:text-right' : ''}`}>
                    <span className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs font-bold rounded-full mb-2">{item.period}</span>
                    <h4 className="text-lg font-bold text-gray-900">{item.role}</h4>
                    <p className="text-gray-600 text-sm">{item.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Vision & Message */}
          <div className="grid md:grid-cols-2 gap-8 mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-3xl p-8 border border-gray-200"
            >
              <Target className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">{founderStory.vision}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white relative overflow-hidden"
            >
              <Quote className="w-12 h-12 text-red-500 mb-4" />
              <p className="text-lg leading-relaxed italic mb-6">"{founderStory.message}"</p>
              <div className="border-t border-white/10 pt-4">
                <div className="font-bold">{founderStory.name}</div>
                <div className="text-gray-400 text-sm">{founderStory.role}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Awards & Honours list */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Awards &amp; Honours</h2>
            <p className="text-xl text-gray-600">A legacy of excellence recognized across India</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {founderAwards.map((award, index) => (
              <motion.div
                key={index}
                data-testid={`founder-award-${index}`}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 flex items-start gap-4 border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Medal className="w-6 h-6 text-white" />
                </div>
                <p className="text-gray-800 font-semibold leading-snug pt-1">{award}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievement highlights */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Award, num: '30+', label: 'Years of Experience' },
              { icon: Trophy, num: '9+', label: 'Industry Awards' },
              { icon: Star, num: 'Excellence', label: 'in Technical Training' }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -6 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{item.num}</div>
                <div className="text-gray-600">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Award Gallery</h2>
            <p className="text-xl text-gray-600">Moments of recognition and pride</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {awards.map((award, index) => (
              <motion.div
                key={award.id}
                data-testid={`award-card-${award.id}`}
                className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 bg-white"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setSelected(award)}
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={award.image}
                    alt={award.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute top-0 left-0 right-0 aspect-[4/3] bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <ZoomIn className="w-7 h-7 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{award.title}</h3>
                  <p className="text-gray-600 text-sm">{award.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <button
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.div
              className="max-w-4xl w-full"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selected.image} alt={selected.title} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <div className="text-center mt-6">
                <h3 className="text-2xl font-bold text-white mb-2">{selected.title}</h3>
                <p className="text-gray-300">{selected.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Awards;
